/** 邮箱登录相关 —— 初始化、验证码、登录、注册、重置密码等 */
/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import md5 from 'js-md5';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import { encryptAes, loginRsaEncrypt, handleTCaptcha, jsonParser } from '@/lib/utils';
import {
    emailInit,
    sendCode,
    picCheckSendCode,
    staticLogin,
    register,
    resetPassword,
    captchaLogin,
    picCheckLogin,
} from '@/api/login';
import Logs from '@/lib/logs';
import { EmailType } from '@/enums';
import debounce from 'lodash/debounce';
import isFunction from 'lodash/isFunction';
import isPlainObject from 'lodash/isPlainObject';

export default {
    data() {
        return {
            ueAppId: 'MobileAb-ACEmail',
            flowIdList: {
                [EmailType.StaticLogin]: '',
                [EmailType.CodeLogin]: '',
                [EmailType.Register]: '',
                [EmailType.ResetPassword]: '',
                [EmailType.RevokeAccount]: '',
            },
            emailMd5: '',
            timer: null,
            captchaText: this.$t('login.sendCaptcha'),
            captchaDisabled: false,
            captchaInstance: null, // 天御验证码实例
            captchaOptions: {}, // 天御外观配置参数
            beforeCaptchaMount() {}, // 拉起天御验证码前，触发的回调
            onCaptchaSuccess() {}, // 天御验证成功
            onCaptchaFailed() {}, // 天御验证失败
            errorCaptchaCaptured(err) {
                // 天御加载失败的回调
                console.log('captcha load failed: ', err);
            },
        };
    },
    methods: {
        setDisabled(disabled) {
            this.captchaDisabled = disabled;
        },

        // 结束重置倒计时
        end() {
            clearInterval(this.timer);
            this.captchaText = this.$t('login.sendCaptcha');
            this.setDisabled(false);
        },

        // 倒计时（短信发送成功后，再倒计时）
        countdown(time = 60, initTime = 60) {
            this.captchaText = this.$t('login.reSendCaptcha', { seconds: time });
            this.setDisabled(true);
            this.timer = setInterval(() => {
                if (time > 0) {
                    time -= 1;
                    this.captchaText = this.$t('login.reSendCaptcha', { seconds: time });
                } else {
                    this.end();
                    time = initTime;
                }
            }, 1000);

            const web_pageid = `ghome-web-${this.$route.name === 'Login' ? 'login' : 'dn2-login'}`;
            Logs.imgLog_Button({
                web_pageid,
                web_buttonid: 'send-sms',
            });
        },

        /**
         * @description 邮箱业务初始化（Email 加密相关）
         * @param {string} email 邮箱
         * @param {string} type 邮箱业务类型，详见 enums -> EmailType
         * @param {Object} options 其他请求参数
         */
        async ueInitClient(email, type, options) {
            // step 1: 用 md5 加密后的 email 获取 key
            // const hashJson = [md5(email).toUpperCase()];
            this.emailMd5 = md5(email).toUpperCase();
            // prettier-ignore
            const appId = this.$route.query.appId || jsonParser(sessionStorage.getItem('appId')) || '';

            const params = {
                ueAppId: encodeURIComponent(`${this.ueAppId}#${appId}`),
                hash: this.emailMd5,
                type,
                ...pickBy(options, item => item), // 过滤空值
            };
            const initEmail = await emailInit(params);
            const keyArr = (initEmail.key || '').split('&#124;');
            Object.assign(this.flowIdList, { [type]: initEmail.flowId });

            // step 2: 用 key 再加密 email
            const emailAes = encryptAes(email, keyArr[0]) || '';

            return {
                cipherUserInfo: emailAes,
                cipherTextType: 2,
                hash: this.emailMd5,
            };
        },

        /**
         * @description 开始倒计时（邮箱初始化 + 短信发送 + 图验）
         * @param {string} email 邮箱
         * @param {string} emailType 邮箱业务类型，详见 enums -> EmailType
         * @param {Object} options 其他请求参数
         */
        start: debounce(async function (email, emailType, options = {}) {
            clearInterval(this.timer);

            // step 1: 开启禁用（成功继续走下去，失败解除禁用）
            this.setDisabled(true);

            try {
                const {
                    flowId, // 其他流程的 flowId，用于插入到邮箱验证流程中
                    captchaOptions,
                    beforeCaptchaMount,
                    errorCaptchaCaptured,
                    onCaptchaSuccess,
                    onCaptchaFailed,
                } = options;

                if (isPlainObject(captchaOptions)) this.captchaOptions = captchaOptions;
                if (isFunction(beforeCaptchaMount)) this.beforeCaptchaMount = beforeCaptchaMount;
                if (isFunction(onCaptchaSuccess)) this.onCaptchaSuccess = onCaptchaSuccess;
                if (isFunction(onCaptchaFailed)) this.onCaptchaFailed = onCaptchaFailed;
                if (isFunction(errorCaptchaCaptured)) this.errorCaptchaCaptured = errorCaptchaCaptured; // prettier-ignore

                // step 2: 邮箱业务初始化
                const cipherInfo = await this.ueInitClient(email, emailType, { flowId });

                // step 3: 请求发送短信，发送成功开始倒计时
                const captchaInfo = await sendCode({
                    flowId: this.flowIdList[emailType],
                    email: JSON.stringify(cipherInfo),
                });

                // step 4：处理 captchaInfo（8：需要图验，其他：无需图验）
                const { nextAction = '', captchaParams } = captchaInfo;
                // step 4.1：拉起天御图验
                if (+nextAction === 8) {
                    this.beforeCaptchaMount();
                    this.captchaInstance = handleTCaptcha(
                        this.getCaptchaAppId(captchaParams),
                        data => this.handleTCaptchaSuccess(data, emailType),
                        initError => {
                            this.setDisabled(false);
                            this.errorCaptchaCaptured(initError);
                        },
                        this.captchaOptions
                    );
                } else {
                    // step 4.2：短信发送成功，开始倒计时;
                    this.countdown();
                }
            } catch (error) {
                this.setDisabled(false);
            }
        }, 800),

        getCaptchaAppId(captchaParams) {
            return get(jsonParser(captchaParams), 'tyData.appId');
        },

        // 天御验证成功回调 —— 发送SMS短信
        async handleTCaptchaSuccess(res, emailType) {
            // 成功
            if (res.ret === 0) {
                this.onCaptchaSuccess();
                try {
                    const captchaInfo = await picCheckSendCode({
                        flowId: this.flowIdList[emailType],
                        outInfo: JSON.stringify({
                            tyData: {
                                ticket: res?.ticket || '',
                                randStr: res?.randstr || '',
                            },
                        }),
                    });

                    const { nextAction = '', captchaParams } = captchaInfo;
                    if (+nextAction === 8) {
                        this.beforeCaptchaMount();
                        this.captchaInstance = handleTCaptcha(
                            this.getCaptchaAppId(captchaParams),
                            data => this.handleTCaptchaSuccess(data, emailType),
                            initError => {
                                this.setDisabled(false);
                                this.errorCaptchaCaptured(initError);
                            },
                            this.captchaOptions
                        );
                    } else {
                        this.countdown();
                    }
                } catch (error) {
                    this.onCaptchaFailed();
                    this.setDisabled(false);
                }
            } else {
                // 用户主动取消（ret === 2）或组件加载错误
                if (res.errorCode) {
                    console.log(`${res.errorCode}：${res.errorMessage}`);
                }
                this.onCaptchaFailed();
                this.setDisabled(false);
            }
        },

        // 邮箱静密登录
        async emailStaticLogin(form, callback) {
            // step 1: 静密登录
            const {
                nextAction = '',
                captchaParams,
                ...userInfo
            } = await staticLogin({
                flowId: this.flowIdList[EmailType.StaticLogin],
                email: JSON.stringify(form.email),
                password: loginRsaEncrypt(form.password),
            });

            // step 2.1: 如果 captchaInfo.nextAction = 8，则需要天御图验
            if (+nextAction === 8) {
                // step 3.1：图验通过，调用 picCheckLogin 再次登录
                this.beforeCaptchaMount();
                this.captchaInstance = handleTCaptcha(
                    this.getCaptchaAppId(captchaParams),
                    data => this.handleTLoginSuccess(data, callback),
                    initError => this.errorCaptchaCaptured(initError),
                    this.captchaOptions
                );
            } else {
                // step 2.2：不等于 8 即登录成功，获取用户信息
                callback(userInfo);
            }
        },

        // 天御验证成功回调 —— 静密登录
        async handleTLoginSuccess(res, callback) {
            // 成功
            if (res.ret === 0) {
                this.onCaptchaSuccess();
                const {
                    nextAction = '',
                    captchaParams,
                    ...userInfo
                } = await picCheckLogin({
                    flowId: this.flowIdList[EmailType.StaticLogin],
                    outInfo: JSON.stringify({
                        tyData: {
                            ticket: res?.ticket || '',
                            randStr: res?.randstr || '',
                        },
                    }),
                });

                // 如果 captchaInfo.nextAction 还是 8，则继续图验循环
                if (+nextAction === 8) {
                    this.beforeCaptchaMount();
                    this.captchaInstance = handleTCaptcha(
                        this.getCaptchaAppId(captchaParams),
                        data => this.handleTLoginSuccess(data, callback),
                        initError => this.errorCaptchaCaptured(initError),
                        this.captchaOptions
                    );
                } else {
                    // 直到不等于 8（登录成功），跳出循环，获取用户信息
                    callback(userInfo);
                }
            } else if (res.errorCode) {
                // 组件加载错误
                console.log(`${res.errorCode}：${res.errorMessage}`);
                this.onCaptchaFailed();
            }
        },

        // 邮箱验证码登录
        emailCaptchaLogin(form) {
            return captchaLogin({
                flowId: this.flowIdList[EmailType.CodeLogin],
                hash: this.emailMd5,
                emailCode: form.captcha,
            });
        },

        // 注册
        emailRegister(form) {
            return register({
                flowId: this.flowIdList[EmailType.Register],
                emailCode: form.captcha,
                hash: this.emailMd5,
                password: loginRsaEncrypt(form.password),
            });
        },

        // 重置密码
        emailResetPassword(form) {
            return resetPassword({
                flowId: this.flowIdList[EmailType.ResetPassword],
                emailCode: form.captcha,
                hash: this.emailMd5,
                password: loginRsaEncrypt(form.password),
            });
        },
    },
};
